import type { ChecklistAPI, ChecklistContextAPI, ChecklistFieldAPI, ChecklistsNamesAPI, ChecklistsPageAPI, ChecklistVehicleAPI } from './Definitions/APITypes';
import {
  type Checklist,
  type ChecklistContext,
  type ChecklistField,
  type ChecklistFieldContent,
  ChecklistFieldType,
  type ChecklistSelectContent,
  type ChecklistsNames,
  type ChecklistsPage,
  type ChecklistVehicle,
  type TemplateChecklist,
} from './Definitions/Types';

const templateChecklistSelectOptionContent = (optionsContent?: ChecklistSelectContent) => {
  if (optionsContent) {
    return optionsContent.options.map((optionContent) => ({
      label: optionContent.label || '',
      checked: null,
    }));
  }

  return {
    label: '',
    checked: null,
  };
};

const templateChecklistFieldContent = (field: {
  type: ChecklistFieldType;
  content: ChecklistFieldContent;
}) => {
  switch (field.type) {
    case ChecklistFieldType.CHECK:
      return { checked: null };
    case ChecklistFieldType.TEXT:
      return { value: null };
    case ChecklistFieldType.MULTI_SELECT:
    case ChecklistFieldType.SINGLE_SELECT:
      return {
        options: templateChecklistSelectOptionContent('options' in field.content ? field.content : undefined),
      };
  }
};

const checklistVehicleTransformer = (checklistVehicleAPI: ChecklistVehicleAPI): ChecklistVehicle => {
  const { device_id: deviceId, license_plate: licensePlate, brand, model } = checklistVehicleAPI;

  return {
    deviceId,
    ...(licensePlate && { licensePlate }),
    ...(brand && { brand }),
    ...(model && { model }),
  };
};

const checklistContextTransformer = (checklistContextAPI: ChecklistContextAPI): ChecklistContext => {
  const { app_version: appVersion, vehicle } = checklistContextAPI;

  return {
    appVersion,
    ...(vehicle && { vehicle: checklistVehicleTransformer(vehicle) }),
  };
};

const checklistFieldTransformer = (checklistFieldAPI: ChecklistFieldAPI): ChecklistField => {
  const { id, type, required, title, content } = checklistFieldAPI;

  return { id, type, required, title, content };
};

export const checklistTransformer = (checklistAPI: ChecklistAPI): Checklist => {
  const { id, version, name, template, created_at: createdAt, created_by: createdBy, fields, template_id: templateId, context } = checklistAPI;

  return {
    id,
    version,
    name,
    template,
    createdAt,
    createdBy,
    fields: fields.map(checklistFieldTransformer),
    templateId,
    context: context ? checklistContextTransformer(context) : null,
  };
};

export const checklistsPageTransformer = (checklistsPage: ChecklistsPageAPI): ChecklistsPage => {
  const checklistsPageContent = checklistsPage.page;

  return {
    page: {
      content: checklistsPageContent.content.map((value) => checklistTransformer(value)),
      last: checklistsPageContent.last,
      totalPages: checklistsPageContent.total_pages,
      totalElements: checklistsPageContent.total_elements,
      numberOfElements: checklistsPageContent.number_of_elements,
      number: checklistsPageContent.number,
      size: checklistsPageContent.size,
      first: checklistsPageContent.first,
      empty: checklistsPageContent.empty,
    },
  };
};

export const checklistsNamesTransformer = (checklistsNames: ChecklistsNamesAPI): ChecklistsNames => ({
  names: checklistsNames.names,
});

export const templateChecklistTransformer = (templateChecklist: TemplateChecklist) => ({
  ...templateChecklist,
  fields: templateChecklist.fields.map((field) => ({
    ...field,
    content: JSON.stringify(templateChecklistFieldContent(field)),
  })),
});
